import CompaniesFilterComponents from "@/components/LazoInboxChatComponents/CompaniesFilterComponents";
import CommonButton, { CommonButtonSecondarySx } from "@/components/utils/CommonButton";
import { FunnelIcon } from "@/components/utils/icons/FunnelIcon";
import { Colors } from "@/enums/Colors";
import { LazoConversationReadStatus } from "@/enums/LazoConversationReadStatus";
import { LazoThreadConversationStatus } from "@/enums/LazoThreadConversationStatus";
import Tenant from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/Tenant";
import { SmallLazoTeamUser } from "@/models/SmallLazoTeamUser";
import { CloseOutlined } from "@mui/icons-material";
import {
  Box,
  Fade,
  Modal,
  Popper,
  SxProps,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  styled,
  toggleButtonClasses,
  toggleButtonGroupClasses,
  typographyClasses,
} from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { GeneralPortalFilters, PortalFilters } from "./PortalChatContext";
import { PortalEmployeesFilterSelector } from "./PortalEmployeesFilterSelector";

type Props = {
  filters: PortalFilters;
  setFilters: Dispatch<SetStateAction<PortalFilters>>;
};

export function PortalChatListFilters(props: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [openTenantSelector, setOpenTenantsSelector] = useState<boolean>(false);

  const [openEmployeesSelector, setOpenEmployeesSelector] = useState<boolean>(false);
  const [employees, setEmployees] = useState<SmallLazoTeamUser[]>([]);

  const menuRef = useRef<HTMLDivElement | null>(null);

  function onPickEmployeesClick() {
    setOpenEmployeesSelector(true);
  }

  function onClearEmployeesClick() {
    props.setFilters((prev) => ({ ...prev, employeesExternalIds: [] }));
    setEmployees([]);
  }

  function onPickCompaniesClick() {
    setOpenTenantsSelector(true);
  }

  function onClearCompaniesClick() {
    props.setFilters((prev) => ({ ...prev, tenantExternalIds: [] }));
    setTenants([]);
  }

  function handleClick(ev: React.MouseEvent<HTMLDivElement>) {
    if (anchorEl !== null) {
      setAnchorEl(null);
      return;
    }

    setAnchorEl(ev.currentTarget);
  }

  function handleClickOutside(ev: MouseEvent) {
    if (openTenantSelector || openEmployeesSelector) return;
    if (!anchorEl.contains(ev.target as Node) && !menuRef.current.contains(ev.target as Node)) {
      setAnchorEl(null);
    }
  }

  function onStatusChange(_: any, v: LazoThreadConversationStatus | null) {
    props.setFilters((prev) => ({ ...prev, status: v }));
  }

  function onReadStatusChange(_: any, v: LazoConversationReadStatus | null) {
    props.setFilters((prev) => ({ ...prev, readStatus: v }));
  }

  useEffect(() => {
    if (anchorEl) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [anchorEl, openTenantSelector, openEmployeesSelector]);

  const open = Boolean(anchorEl);
  const id = "portal-filter-menu";

  return (
    <>
      <Box aria-described-by={id} onClick={handleClick} display="flex" alignItems="center">
        <FunnelIcon sx={{ fontSize: "1.375rem", cursor: "pointer" }} />
      </Box>
      <StyledPopper id={id} open={open} anchorEl={anchorEl} disablePortal transition>
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={100}>
            <PopperContent ref={menuRef}>
              <PopperHeader>
                <Typography>Filter</Typography>
                <StyledCloseOutlined onClick={() => setAnchorEl(null)} />
              </PopperHeader>
              <Box display={"flex"} gap={"0.25rem"} flexDirection={"column"}>
                <Typography fontWeight={"400"} fontSize={"0.75rem"} color={Colors.LIGHT_GRAY_8A8E93}>
                  General
                </Typography>
                <ToggleButtonsContainer
                  value={props.filters.generalPortalFilters}
                  onChange={(_, v) => props.setFilters((prev) => ({ ...prev, generalPortalFilters: v || GeneralPortalFilters.ALL }))}
                  exclusive
                >
                  <StyledToggleButton value={GeneralPortalFilters.ALL} key={GeneralPortalFilters.ALL}>
                    <Typography>All</Typography>
                  </StyledToggleButton>
                  <StyledToggleButton value={GeneralPortalFilters.MENTIONS} key={GeneralPortalFilters.MENTIONS}>
                    <Typography>Mentions</Typography>
                  </StyledToggleButton>
                  <StyledToggleButton value={GeneralPortalFilters.READ_ONLY} key={GeneralPortalFilters.READ_ONLY}>
                    <Tooltip title={"This will show all conversation from other Lazo members, but read only"}>
                      <Typography>All Others</Typography>
                    </Tooltip>
                  </StyledToggleButton>
                </ToggleButtonsContainer>
              </Box>
              <Box display={"flex"} gap={"0.25rem"} flexDirection={"column"}>
                <Typography fontWeight={"400"} fontSize={"0.75rem"} color={Colors.LIGHT_GRAY_8A8E93}>
                  Status
                </Typography>
                <ToggleButtonsContainer exclusive value={props.filters.status} onChange={onStatusChange}>
                  <StyledToggleButton value={null} key={"All"}>
                    <Typography>All</Typography>
                  </StyledToggleButton>
                  <StyledToggleButton value={LazoThreadConversationStatus.OPEN} key={LazoThreadConversationStatus.OPEN}>
                    <Typography>Open</Typography>
                  </StyledToggleButton>
                  <StyledToggleButton value={LazoThreadConversationStatus.ANSWERED} key={LazoThreadConversationStatus.ANSWERED}>
                    <Typography>Answered</Typography>
                  </StyledToggleButton>
                  <StyledToggleButton value={LazoThreadConversationStatus.FINALIZED} key={LazoThreadConversationStatus.FINALIZED}>
                    <Typography>Finalized</Typography>
                  </StyledToggleButton>
                </ToggleButtonsContainer>
              </Box>
              <Box display={"flex"} gap={"0.25rem"} flexDirection={"column"}>
                <Typography fontWeight={"400"} fontSize={"0.75rem"} color={Colors.LIGHT_GRAY_8A8E93}>
                  Read Status
                </Typography>
                <ToggleButtonsContainer exclusive value={props.filters.readStatus} onChange={onReadStatusChange}>
                  <StyledToggleButton value={null} key={"All"}>
                    <Typography>All</Typography>
                  </StyledToggleButton>
                  <StyledToggleButton value={LazoConversationReadStatus.READ} key={LazoConversationReadStatus.READ}>
                    <Typography>Read</Typography>
                  </StyledToggleButton>
                  <StyledToggleButton value={LazoConversationReadStatus.UNREAD} key={LazoConversationReadStatus.UNREAD}>
                    <Typography>Unread</Typography>
                  </StyledToggleButton>
                </ToggleButtonsContainer>
              </Box>
              <Box display={"flex"} gap={"0.25rem"} flexDirection={"column"}>
                <Typography fontWeight={"400"} fontSize={"0.75rem"} color={Colors.LIGHT_GRAY_8A8E93}>
                  Person
                </Typography>
                {employees.length === 0 ? (
                  <Typography fontWeight="400">No employees selected</Typography>
                ) : (
                  <Typography fontWeight="400">{employees.map((t) => t.firstName + " " + t.lastName).join(", ")}</Typography>
                )}
                <Box display="flex" alignItems="center" width="100%" gap="0.5rem">
                  <CommonButton onClick={onPickEmployeesClick} sx={CommonButtonSecondarySx}>
                    <Typography fontWeight="400" fontSize="0.75rem">
                      Pick Employees
                    </Typography>
                  </CommonButton>
                  <CommonButton onClick={onClearEmployeesClick} sx={CommonButtonSecondarySx}>
                    <Typography fontWeight="400" fontSize="0.75rem">
                      Clear
                    </Typography>
                  </CommonButton>
                </Box>
              </Box>
              <Box display={"flex"} gap={"0.25rem"} flexDirection={"column"}>
                <Typography fontWeight={"400"} fontSize={"0.75rem"} color={Colors.LIGHT_GRAY_8A8E93}>
                  Companies
                </Typography>
                {tenants.length === 0 ? (
                  <Typography fontWeight="400">No companies selected</Typography>
                ) : (
                  <Typography fontWeight="400">{tenants.map((t) => t.name).join(", ")}</Typography>
                )}
                <Box display="flex" alignItems="center" width="100%" gap="0.5rem">
                  <CommonButton onClick={onPickCompaniesClick} sx={CommonButtonSecondarySx}>
                    <Typography fontWeight="400" fontSize="0.75rem">
                      Pick Companies
                    </Typography>
                  </CommonButton>
                  <CommonButton onClick={onClearCompaniesClick} sx={CommonButtonSecondarySx}>
                    <Typography fontWeight="400" fontSize="0.75rem">
                      Clear
                    </Typography>
                  </CommonButton>
                </Box>
              </Box>
            </PopperContent>
          </Fade>
        )}
      </StyledPopper>
      <Modal onClose={() => setOpenTenantsSelector(false)} open={openTenantSelector} sx={ModalSx}>
        <CompaniesFilterComponents
          selectedExternalIds={props.filters.tenantExternalIds}
          setFilters={props.setFilters}
          setTenantEntities={setTenants}
        />
      </Modal>
      <Modal onClose={() => setOpenEmployeesSelector(false)} open={openEmployeesSelector} sx={ModalSx}>
        <PortalEmployeesFilterSelector setFilters={props.setFilters} selectedEmployees={employees} setSelectedEmployees={setEmployees} />
      </Modal>
    </>
  );
}

const StyledPopper = styled(Popper)(({ theme }) => ({
  padding: "0.75rem",
  boxShadow: "0 -2px 20.1px 0 rgba(101, 101, 101, 0.17)",
  backgroundColor: "#fff",
  borderRadius: "0.75rem",
  zIndex: theme.zIndex.tooltip,
}));

const PopperContent = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  width: "21.25rem",
  gap: "0.75rem",
  [`& .${typographyClasses.root}`]: {
    fontSize: "0.875rem",
    fontWeight: "500",
  },
}));

const PopperHeader = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const StyledCloseOutlined = styled(CloseOutlined)(() => ({
  fontSize: "1rem",
  ":hover": {
    cursor: "pointer",
  },
}));

const ToggleButtonsContainer = styled(ToggleButtonGroup)(() => ({
  display: "flex",
  gap: "0.5rem",
  width: "100%",
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    ":not(:last-of-type :first-of-type)": { borderRadius: "0.25rem" },
    ":is(:last-of-type :first-of-type)": { borderRadius: "0.25rem" },
  },
}));

const ModalSx: SxProps = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const StyledToggleButton = styled(ToggleButton)(() => ({
  [`&.${toggleButtonClasses.root}`]: {
    height: "1.625rem",
    flex: "1",
    padding: 0,
    borderRadius: "0.25rem",
    border: 0,
    transition: "background-color 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: Colors.BUTTON_GRAY_HOVER_E2E6E9,
    },
    textTransform: "none",
    backgroundColor: Colors.LIGHT_GRAY_F4F4F4,
    [`& .${typographyClasses.root}`]: {
      color: Colors.ELEGANT_BLACK,
      fontSize: "0.75rem",
      fontWeight: "500",
    },
    [`&.${toggleButtonClasses.selected}`]: {
      backgroundColor: Colors.LAZO_PURPLE,
      [`& .${typographyClasses.root}`]: {
        color: "#fff",
      },
      "&:hover": {
        backgroundColor: Colors.LAZO_PURPLE,
      },
    },
  },
}));
